const BASE = import.meta.env.VITE_API_URL ?? '/api'

import { authHeaders } from './auth'
import { sessionId, error as logError, debug as logDebug } from './logger'

function sessionHeader(): Record<string, string> {
  return { 'X-Session-Id': sessionId }
}

/** Verify the current token against the backend. Returns the user or null. */
export async function fetchMe(): Promise<{ email: string; role: string } | null> {
  const res = await fetch(`${BASE}/auth/me`, { headers: { ...authHeaders(), ...sessionHeader() } })
  if (!res.ok) return null
  return res.json()
}

export interface HistoryMessage {
  role: 'user' | 'assistant'
  content: string | unknown[]  // string for plain text, block array for full Anthropic format
}

export interface SavedWrite {
  target_id: string
  target_name: string
}

export interface StreamDonePayload {
  saved: SavedWrite[]
  warnings: string[]
  messages?: HistoryMessage[]  // full Anthropic message list including tool calls/results
}

// ── Open dates ────────────────────────────────────────────────────────────────

export interface VenueOpenDates {
  venue: string
  open_dates: string[]      // ISO date strings with no show booked
  last_show_date: string
  total_shows: number
}

export interface OpenDatesPayload {
  venues: VenueOpenDates[]
  total_venues: number
  days_searched: number
}

export async function streamChat(
  message: string,
  history: HistoryMessage[],
  onDelta: (delta: string) => void,
  onStatus?: (line: string) => void,
  onCandidate?: (candidate: Candidate) => void,
  onOpenDates?: (data: OpenDatesPayload) => void,
  signal?: AbortSignal,
  onCandidateUpdate?: (candidate: Candidate) => void,
): Promise<StreamDonePayload> {
  logDebug('chat_stream_start', { msg_len: message.length, history_turns: history.length })
  const res = await fetch(`${BASE}/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...sessionHeader() },
    body: JSON.stringify({ message, history }),
    signal,
  })
  if (!res.ok) {
    const text = await res.text()
    logError('chat_stream_http_error', { status: res.status, body: text.slice(0, 200) })
    throw new Error(`Request failed (${res.status}): ${text}`)
  }

  const reader = res.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let result: StreamDonePayload = { saved: [], warnings: [] }

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const payload = JSON.parse(line.slice(6))
        if (payload.type === 'text') {
          onDelta(payload.delta)
        } else if (payload.type === 'status') {
          onStatus?.(payload.text)
        } else if (payload.type === 'candidate') {
          onCandidate?.(payload.candidate as Candidate)
        } else if (payload.type === 'candidate_update') {
          onCandidateUpdate?.(payload.candidate as Candidate)
        } else if (payload.type === 'open_dates') {
          onOpenDates?.(payload.data as OpenDatesPayload)
        } else if (payload.type === 'done') {
          result = { saved: payload.saved, warnings: payload.warnings, messages: payload.messages }
        } else if (payload.type === 'error') {
          logError('chat_stream_server_error', { message: payload.message })
          throw new Error(payload.message)
        }
      }
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      return result
    }
    throw e
  }

  return result
}

// ── Candidates ────────────────────────────────────────────────────────────────

export interface CandidateConnection {
  item_id: string
  from: string
  to: string
  type: string
  direction: string
  notes: string
}

export interface Candidate {
  item_id: string
  label: string
  type: string
  description: string
  tags: string
  genre: string
  links: string  // newline-separated URLs
  location: string
  email: string
  phone: string
  score: string
  source_notes: string
  date_added: string
  connections: CandidateConnection[]
}

export async function fetchCandidates(): Promise<Candidate[]> {
  const res = await fetch(`${BASE}/candidates`, { headers: authHeaders() })
  if (!res.ok) throw new Error(`Failed to load candidates (${res.status})`)
  const data = await res.json()
  return data.candidates ?? []
}

export async function approveCandidate(itemId: string, label?: string): Promise<void> {
  const res = await fetch(`${BASE}/candidates/${encodeURIComponent(itemId)}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ label }),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Approve failed (${res.status}): ${text}`)
  }
}

export async function deleteCandidate(itemId: string): Promise<void> {
  const res = await fetch(`${BASE}/candidates/${encodeURIComponent(itemId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Delete failed (${res.status}): ${text}`)
  }
}

export async function skipCandidate(itemId: string, label?: string, reason = ''): Promise<void> {
  const res = await fetch(`${BASE}/candidates/${encodeURIComponent(itemId)}/skip`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ label, reason }),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Skip failed (${res.status}): ${text}`)
  }
}

// ── Sync ──────────────────────────────────────────────────────────────────────

export async function syncFromGoogle(dryRun = false): Promise<void> {
  const res = await fetch(`${BASE}/sync/from-google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ dry_run: dryRun }),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Sync from Google failed (${res.status}): ${text}`)
  }
}

export async function clearCache(): Promise<void> {
  const res = await fetch(`${BASE}/cache/clear`, {
    method: 'POST',
    headers: { ...authHeaders() },
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Clear cache failed (${res.status}): ${text}`)
  }
}

export async function syncToGoogle(dryRun = false): Promise<void> {
  const res = await fetch(`${BASE}/sync/to-google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ dry_run: dryRun }),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Sync to Google failed (${res.status}): ${text}`)
  }
}

/** Fetch the Kumu embed URL from the backend (auth-gated, never in the JS bundle). */
export async function fetchKumuEmbedUrl(): Promise<string> {
  const res = await fetch(`${BASE}/kumu-embed-url`, { headers: authHeaders() })
  if (!res.ok) return ''
  const data = await res.json()
  return data.url ?? ''
}

// ── Scene elements (paginated kumu_elements) ──────────────────────────────────

export interface SceneElementsResponse {
  elements: Candidate[]
  total: number
  page: number
  limit: number
  has_more: boolean
}

export async function fetchSceneElements(page = 1, limit = 20): Promise<SceneElementsResponse> {
  const res = await fetch(`${BASE}/scene-elements?page=${page}&limit=${limit}`, { headers: authHeaders() })
  if (!res.ok) throw new Error(`Failed to load scene elements (${res.status})`)
  return res.json()
}

// ── Sheet data (read-only table view) ─────────────────────────────────────────

export interface SheetDataResponse {
  header: string[]
  rows: string[][]
  total: number
  page: number
  limit: number
  has_more: boolean
}

export async function fetchSheetData(targetId: string, page = 1, limit = 1000): Promise<SheetDataResponse> {
  logDebug('fetch_sheet_data', { targetId, page })
  const res = await fetch(`${BASE}/sheet-data/${encodeURIComponent(targetId)}?page=${page}&limit=${limit}`, { headers: { ...authHeaders(), ...sessionHeader() } })
  if (!res.ok) {
    const text = await res.text()
    logError('fetch_sheet_data_error', { targetId, status: res.status, body: text.slice(0, 200) })
    throw new Error(`Failed to load sheet (${res.status})`)
  }
  return res.json()
}

// ── Google Doc content ────────────────────────────────────────────────────────

export async function fetchDocContent(targetId: string): Promise<{ text: string }> {
  logDebug('fetch_doc_content', { targetId })
  const res = await fetch(`${BASE}/doc-content/${encodeURIComponent(targetId)}`, { headers: { ...authHeaders(), ...sessionHeader() } })
  if (!res.ok) {
    const text = await res.text()
    logError('fetch_doc_content_error', { targetId, status: res.status, body: text.slice(0, 200) })
    throw new Error(`Failed to load document (${res.status})`)
  }
  return res.json()
}

export async function triggerResearchNetwork(
  onStatus: (line: string) => void,
  days = 180,
  maxCandidates = 50,
): Promise<void> {
  const res = await fetch(`${BASE}/workflows/research-network`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ days, max_candidates: maxCandidates }),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Workflow failed (${res.status}): ${text}`)
  }

  const reader = res.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue
      const payload = JSON.parse(line.slice(6))
      if (payload.type === 'status') onStatus(payload.text)
    }
  }
}
